import { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function PrerequisitesEditor() {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [prerequisites, setPrerequisites] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedPrerequisite, setSelectedPrerequisite] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      fetchPrerequisites();
    }
  }, [selectedModule]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/modules');
      
      // AdminModuleController returns modules directly as an array
      const modulesData = Array.isArray(response.data) ? response.data : response.data.modules || [];
      const sortedModules = modulesData.sort((a, b) => a.order - b.order);
      setModules(sortedModules);
      
      if (sortedModules.length > 0) {
        setSelectedModule(sortedModules[0]);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrerequisites = async () => {
    if (!selectedModule) return;

    try {
      const response = await api.get(`/admin/prerequisites/modules/${selectedModule.id}`);
      setPrerequisites(response.data.prerequisites);
      
      // Filter out current module and existing prerequisites from available modules
      const existingIds = response.data.prerequisites.map(p => p.id);
      const available = modules.filter(
        m => m.id !== selectedModule.id && !existingIds.includes(m.id)
      );
      setAvailableModules(available);
      setSelectedPrerequisite(available.length > 0 ? available[0].id : '');
    } catch (error) {
      console.error('Error fetching prerequisites:', error);
      toast.error('Failed to load prerequisites');
    }
  };

  const addPrerequisite = async () => {
    if (!selectedPrerequisite) {
      toast.error('Please select a module to add as prerequisite');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/admin/prerequisites/modules/${selectedModule.id}`, {
        prerequisite_module_id: selectedPrerequisite
      });
      
      toast.success('Prerequisite added successfully');
      fetchPrerequisites();
    } catch (error) {
      console.error('Error adding prerequisite:', error);
      const message = error.response?.data?.message || 'Failed to add prerequisite';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const removePrerequisite = async (prerequisiteId) => {
    if (!confirm('Are you sure you want to remove this prerequisite?')) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/admin/prerequisites/modules/${selectedModule.id}/prerequisites/${prerequisiteId}`);
      
      toast.success('Prerequisite removed successfully');
      fetchPrerequisites();
    } catch (error) {
      console.error('Error removing prerequisite:', error);
      toast.error('Failed to remove prerequisite');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Prerequisites Editor</h1>
        <p className="text-gray-600 mt-2">
          Manage module prerequisites to control the learning path sequence
        </p>
      </div>

      {/* Help Banner */}
      <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-900">How to Set Prerequisites</h3>
            <div className="mt-2 text-sm text-blue-800">
              <ol className="list-decimal list-inside space-y-1">
                <li><strong>Select a module</strong> from the list on the left (the one students need to unlock)</li>
                <li><strong>Choose which module(s)</strong> students must complete first from the dropdown</li>
                <li><strong>Click "Add"</strong> to require that prerequisite</li>
                <li><strong>Example:</strong> To make students complete "M01" before "M02", select M02 on the left, then add M01 as its prerequisite</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Module Selector */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Select Module to Edit</h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Step 1</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Choose the module that you want to add prerequisites to
            </p>
            
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {modules.map((module) => (
                <button
                  key={module.id}
                  onClick={() => setSelectedModule(module)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    selectedModule?.id === module.id
                      ? 'border-cte-blue bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{module.code}</p>
                      <p className="text-sm text-gray-600 mt-1">{module.title}</p>
                    </div>
                    {selectedModule?.id === module.id && (
                      <div className="w-2 h-2 bg-cte-blue rounded-full mt-2"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Prerequisites Management */}
        <div className="lg:col-span-2">
          {selectedModule ? (
            <div className="space-y-6">
              {/* Current Module Info */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Setting Prerequisites For:
                  </h2>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Current Selection</span>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border-2 border-cte-blue">
                  <p className="font-medium text-gray-900">{selectedModule.code}</p>
                  <p className="text-gray-600">{selectedModule.title}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    👆 Students will need to complete the prerequisites below before accessing this module
                  </p>
                </div>
              </div>

              {/* Add New Prerequisite */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add Required Module
                  </h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Step 2</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Select a module that students must complete <strong>before</strong> they can access <strong>{selectedModule.code}</strong>
                </p>
                
                {availableModules.length > 0 ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Which module must be completed first?
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      value={selectedPrerequisite}
                      onChange={(e) => setSelectedPrerequisite(e.target.value)}
                      className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cte-blue focus:border-transparent"
                      disabled={saving}
                    >
                      {availableModules.map((module) => (
                        <option key={module.id} value={module.id}>
                          {module.code} - {module.title}
                        </option>
                      ))}
                    </select>
                    
                    <button
                      onClick={addPrerequisite}
                      disabled={saving || !selectedPrerequisite}
                      className="flex items-center justify-center gap-2 px-6 py-2 bg-cte-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                    >
                      {saving ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      ) : (
                        <PlusIcon className="h-5 w-5" />
                      )}
                      Add Prerequisite
                    </button>
                  </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500 bg-gray-50 rounded-lg p-4">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <p>No more modules available to add as prerequisites</p>
                  </div>
                )}
              </div>

              {/* Current Prerequisites List */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Current Prerequisites ({prerequisites.length})
                  </h3>
                  {prerequisites.length > 0 && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                      ✓ Active Rules
                    </span>
                  )}
                </div>
                
                {prerequisites.length > 0 && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>Students must complete these modules first:</strong>
                    </p>
                  </div>
                )}
                
                {prerequisites.length > 0 ? (
                  <div className="space-y-3">
                    {prerequisites.map((prereq) => (
                      <div
                        key={prereq.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{prereq.code}</p>
                          <p className="text-sm text-gray-600">{prereq.title}</p>
                        </div>
                        
                        <button
                          onClick={() => removePrerequisite(prereq.id)}
                          disabled={saving}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Remove prerequisite"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <p>No prerequisites set for this module</p>
                    <p className="text-sm mt-1">Students can access this module immediately</p>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">ℹ️ How Prerequisites Work</h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Students must complete ALL prerequisites before accessing this module</li>
                  <li>• Prerequisites are checked in order (listed above)</li>
                  <li>• Circular dependencies are automatically prevented</li>
                  <li>• Changes take effect immediately for all students</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500">Select a module from the left to manage its prerequisites</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
